/**
 * Price utilities for KitScope
 * All price data comes from the database (populated by the Edge Function cron)
 * No direct browser calls to external APIs
 */

import { supabase } from './supabase'

export interface PriceData {
  date: string
  close: number
}

export interface PriceInfo {
  ticker: string
  currentPrice: number | null
  previousClose: number | null
  currency: string
  dayChange: number | null
  dayChangePct: number | null
}

/**
 * Convert price to pence
 * For UK stocks (.L suffix), prices from DB are already in pence
 * For US stocks, prices are in dollars - convert to pence using FX rate
 */
export function normalizeToPence(
  price: number,
  ticker: string,
  nativeCurrency: string,
  fxRate: number = 1
): number {
  if (ticker.endsWith('.L')) {
    return price
  }
  
  if (nativeCurrency === 'USD') {
    return price * 100 * fxRate
  }
  
  return price * 100
}

/**
 * Fetch current price info from database
 */
export async function fetchPriceInfo(ticker: string): Promise<PriceInfo> {
  const { data, error } = await supabase
    .from('prices')
    .select('close_pence, close_native, fx_rate, date')
    .eq('ticker', ticker)
    .order('date', { ascending: false })
    .limit(2) as { data: Array<{ close_pence: number; close_native: number; fx_rate: number | null; date: string }> | null; error: any }

  if (error || !data || data.length === 0) {
    throw new Error(`No price data available for ${ticker}`)
  }

  const latest = data[0]
  const previous = data.length > 1 ? data[1] : null
  
  const currentPrice = latest.close_pence
  const previousClose = previous?.close_pence ?? null
  const isUK = ticker.endsWith('.L')
  const currency = isUK ? 'GBP' : 'USD'
  
  let dayChange: number | null = null
  let dayChangePct: number | null = null
  
  if (currentPrice && previousClose) {
    dayChange = currentPrice - previousClose
    dayChangePct = (dayChange / previousClose) * 100
  }
  
  return {
    ticker,
    currentPrice: isUK ? currentPrice : (latest.fx_rate ? currentPrice / (latest.fx_rate * 100) : currentPrice),
    previousClose: isUK ? previousClose : (previous?.fx_rate && previousClose ? previousClose / (previous.fx_rate * 100) : previousClose),
    currency,
    dayChange,
    dayChangePct,
  }
}

/**
 * Fetch historical price data from database
 */
export async function fetchPriceHistory(
  ticker: string,
  _range: '1mo' | '3mo' | '6mo' | '1y' | '5y' | 'max' = 'max'
): Promise<PriceData[]> {
  const { data, error } = await supabase
    .from('prices')
    .select('date, close_pence')
    .eq('ticker', ticker)
    .order('date', { ascending: true }) as { data: Array<{ date: string; close_pence: number }> | null; error: any }

  if (error || !data || data.length === 0) {
    throw new Error(`No price history available for ${ticker}`)
  }

  return data.map(p => ({
    date: p.date,
    close: p.close_pence,
  }))
}

/**
 * Get the appropriate ticker format
 */
export function getYahooTicker(ticker: string): string {
  if (ticker.endsWith('.L')) return ticker
  const ukFunds = ['0P000', 'GB00']
  for (const prefix of ukFunds) {
    if (ticker.startsWith(prefix)) return ticker + '.L'
  }
  return ticker
}

/**
 * Fetch FX rate from USD to GBP
 * Uses cached rates from the database (populated by Edge Function cron)
 */
export async function fetchFxRateUsdGbp(): Promise<number> {
  const { data } = await supabase
    .from('fx_rates')
    .select('rate')
    .eq('from_currency', 'USD')
    .eq('to_currency', 'GBP')
    .order('date', { ascending: false })
    .limit(1)
    .single() as { data: { rate: number } | null }
  
  if (data?.rate) return data.rate
  return 0.79 // fallback
}